import React from 'react';
import {
  collectorFacts,
  collectorRecognition,
  collectorValue,
} from '@bilt/domain/collectorTemplate';
import type { PassportCard } from '../../services/api/passportCards';
import { DiamondScoreBadge } from '../ui/DiamondScoreBadge';
import CertifiedBuildSeal from '../ui/CertifiedBuildSeal';
import { BadgeStack } from '../badges/BadgeRenderer';
import LimitedDesignationPlate from '../limited-designation/LimitedDesignationPlate';
import { getBuildLevel } from '../../utils/buildLevel';
import type { IssuedBadge } from '../../types/discoveryBadges';

/** The vehicle face is rendered here for every context, including direct PassportCard callers. */
export default function CollectorVehicleDetails({
  card,
  onOwner,
  onRecognition,
  section = 'details',
}: {
  card: PassportCard;
  onOwner?: () => void;
  onRecognition: () => void;
  section?: 'details' | 'marks';
}) {
  const subject = card.subject;
  if (!subject || card.subject_type !== 'vehicle') return null;
  const facts = subject.facts || {};
  const count = facts.modification_count ?? subject.modification_count;
  const mods = count == null || count === '' ? null : Number(count);
  const grade =
    mods != null && Number.isSafeInteger(mods) && mods >= 0 ? getBuildLevel(mods) : null;
  const badges = collectorRecognition(subject.card_summary?.badges, card.subject_id).map(
    (b) => b.source as unknown as IssuedBadge
  );
  const finish = collectorValue(facts.finish);
  const updated = collectorValue(facts.updated_at);
  const date =
    updated === '-'
      ? null
      : new Date(
          updated.includes('T')
            ? updated
            : updated.replace(' ', 'T') + (updated.includes(' ') ? 'Z' : '')
        );
  const owner = subject.owner_username || collectorValue(facts.builder);
  if (section === 'marks')
    return (
      <div className="ct-photo-marks">
        {grade && (
          <div className="ct-grade">
            <DiamondScoreBadge modCount={mods!} size="sm" variant="plain" />
          </div>
        )}
        <div className="ct-photo-honors">
          <CertifiedBuildSeal
            showPendingForOwner={card.preview_context === 'owner'}
            status={String(facts.certification_status || subject.certification_status || '')}
          />
        </div>
        {(badges.length > 0 || (subject.card_summary?.badge_overflow || 0) > 0) && (
          <div className="ct-recognition">
            <BadgeStack
              badges={badges}
              maxVisible={5}
              size="xs"
              variant="overlap"
              overflow={subject.card_summary?.badge_overflow || 0}
              onOverflowClick={onRecognition}
              onBadgeClick={onRecognition}
            />
          </div>
        )}
      </div>
    );
  return (
    <div className="ct-vehicle-details">
      <div className="ct-finish-line">
        <span className="ct-finish">{finish}</span>
        {grade?.showTierLabel && <span className="ct-tier">{grade.name}</span>}
        {subject.card_summary?.limited_designation && (
          <LimitedDesignationPlate
            designation={subject.card_summary.limited_designation}
            size="card"
          />
        )}
      </div>
      <dl className="ct-facts">
        {collectorFacts('vehicle', 'front', { ...facts, modification_count: count })
          .filter((f) => f.key !== 'torque')
          .map((f) => (
            <div key={f.key} data-fact={f.key}>
              <dd>{f.value}</dd>
              <dt>
                {f.key === 'power' && facts.torque != null ? `${facts.torque} lb-ft` : f.label}
              </dt>
            </div>
          ))}
      </dl>
      <div className="ct-meta">
        {owner &&
          owner !== '-' &&
          (onOwner ? (
            <button type="button" onClick={onOwner}>
              @{owner}
            </button>
          ) : (
            <span className="ct-owner">@{owner}</span>
          ))}
        {facts.view_count != null && <span>{facts.view_count} views</span>}
        {date && !Number.isNaN(date.valueOf()) && (
          <time dateTime={updated}>
            Updated {date.toLocaleDateString('en-US', { timeZone: 'UTC' })}
          </time>
        )}
      </div>
    </div>
  );
}
